import type {
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import axios from "axios";

import type { IQuery } from "@citrine/client";
import ClientBase from "@citrine/client";

import type {
  DocumentAnswer,
  DownloadDocumentLink,
  PrivateDocument,
  PrivateDocumentResponse,
} from ".";

interface DocumentQuery extends IQuery {
  page?: number;
  page_size?: number;
  sort_by?: string;
  order?: string;
}

export class DocumentClient extends ClientBase<
  PrivateDocument,
  string,
  DocumentQuery
> {
  useUpdate!: never;
  useCreate!: never;

  protected constructor(protected teamId?: string) {
    super({
      endpoint: "/api/v1/catalyst/documents",
      findField: "items",
      defaultQuery: {
        page: 1,
        page_size: 20,
        sort_by: undefined,
        order: undefined,
      },
    });
  }

  static getInstance(teamId?: string) {
    return new this(teamId);
  }

  async getDocumentCount() {
    const response = await this.request<PrivateDocumentResponse>({
      url: this.endpoint,
      params: { page_size: 0 },
      headers: this.getReadHeaders(),
    });

    return response.total;
  }

  useGetDocumentCount(
    options?: Omit<UseQueryOptions<number>, "queryKey" | "queryFn">,
  ): UseQueryResult<number | undefined> {
    return useQuery({
      ...options,
      queryKey: [...this.getQueryCacheKey(), "count"],
      queryFn: () => this.getDocumentCount(),
    });
  }

  // Get link to download file from s3
  getDownloadLink(id: string) {
    return this.request<DownloadDocumentLink>({
      url: `${this.endpoint}/${id}/download-url`,
      headers: this.getReadHeaders(),
    });
  }

  useGetDownloadLink(
    fileId: string,
    options?: Omit<
      UseQueryOptions<DownloadDocumentLink>,
      "queryKey" | "queryFn"
    >,
  ): UseQueryResult<DownloadDocumentLink | undefined> {
    return useQuery({
      ...options,
      queryKey: [...this.getQueryCacheKey(), fileId, "download-link"],
      queryFn: () => this.getDownloadLink(fileId),
    });
  }

  // Request to get presigned s3 url to upload document
  getDocumentUploadUrl(file: File) {
    return this.request<{
      url: string;
      id: string;
    }>({
      url: `${this.endpoint}/upload-url`,
      data: this.getDocumentUploadData(file),
      method: "POST",
      headers: this.getWriteHeaders(),
    });
  }

  updateFileContent(file: File, url: string) {
    return axios.request({
      url,
      data: file,
      method: "PUT",
      headers: {
        "Content-Type": file.type,
      },
    });
  }

  // Trigger processing for a document that exists in s3
  async processDocument(id: string) {
    const response = await this.request<PrivateDocument>({
      url: `${this.endpoint}/${id}/process-upload`,
      method: "POST",
      headers: this.getWriteHeaders(),
    });

    this.invalidateAfterMutation(response.id);

    return response;
  }

  useProcessDocument(
    options?: Omit<
      UseMutationOptions<PrivateDocument, AxiosError, string>,
      "mutationFn"
    >,
  ) {
    return useMutation({
      ...options,
      mutationFn: (id: string) => this.processDocument(id),
    });
  }

  async createDocument(file: File) {
    // Get pre-signed upload url
    const { url: preSignedUrl, id: fileId } =
      await this.getDocumentUploadUrl(file);

    // Upload file
    await this.updateFileContent(file, preSignedUrl);

    // Process file
    return this.processDocument(fileId);
  }

  useCreateDocuments(
    options?: Omit<
      UseMutationOptions<PrivateDocument[], AxiosError, File[]>,
      "mutationFn"
    >,
  ) {
    return useMutation({
      ...options,
      mutationFn: (files: File[]) => {
        const createDocumentPromises = files.map((file) =>
          this.createDocument(file),
        );

        Promise.allSettled(createDocumentPromises).then(
          // invalidate cache
          () => this.invalidateAfterMutation(),
        );

        return Promise.all(createDocumentPromises);
      },
    });
  }

  search(question?: string) {
    return this.request<DocumentAnswer>({
      url: `${this.endpoint}/answer`,
      params: { question },
      headers: this.getReadHeaders(),
    });
  }

  useSearch(options?: UseMutationOptions<DocumentAnswer, AxiosError, string>) {
    return useMutation({
      ...options,
      mutationFn: (question?: string) => this.search(question),
    });
  }

  protected getQueryCachePrefix(): QueryKey {
    // We use the team id in the headers to filter documents but headers are not
    // included in the query key.
    return [...super.getQueryCachePrefix(), this.teamId];
  }

  protected getDocumentUploadData(file: File) {
    return { file_name: file.name };
  }

  protected findConfig(query?: DocumentQuery) {
    return {
      ...super.findConfig(query),
      headers: this.getReadHeaders(),
    };
  }

  protected deleteConfig(id: string) {
    return {
      ...super.deleteConfig(id),
      headers: this.getWriteHeaders(),
    };
  }

  protected getReadHeaders() {
    return this.teamId
      ? {
          "Citrine-Read-Team-Ids": this.teamId,
        }
      : undefined;
  }

  protected getWriteHeaders() {
    return this.teamId
      ? {
          "Citrine-Write-Team-Ids": this.teamId,
        }
      : undefined;
  }
}
